import React, { useContext, useEffect } from 'react'
import {
  AmbientLight,
  DirectionalLight,
  Matrix4,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three'
import { GLTFLoader, SkeletonUtils } from 'three-stdlib'
import mapIcons, { MapIcon } from '../map/icons'
import { MapContext } from './mapContext'
import AssetsContext from '../assets'

interface MarkerProps {
  position: google.maps.LatLng | google.maps.LatLngLiteral
  scale?: number
  icon?: MapIcon
  title?: string
  color?: string
  borderColor?: string
  customIcon?: google.maps.Icon | google.maps.Symbol | {}
  onClick?: (event: google.maps.MapMouseEvent, marker: google.maps.Marker) => void
  onDrag?: (event: google.maps.MapMouseEvent, marker: google.maps.Marker) => void
  onDragEnd?: (event: google.maps.MapMouseEvent, marker: google.maps.Marker) => void
  onDragStart?: (event: google.maps.MapMouseEvent, marker: google.maps.Marker) => void
  label?: string | google.maps.MarkerLabel
  zIndex?: number
}

export default function Marker({
  position,
  icon = 'pin',
  title,
  scale = 1,
  customIcon,
  color = mapIcons[icon].preferredColor,
  borderColor,
  onClick,
  onDrag,
  onDragEnd,
  onDragStart,
  label,
  zIndex,
}: MarkerProps) {
  const assetsContext = useContext(AssetsContext)
  const mapContext = useContext(MapContext)
  if (!mapContext) {
    throw new Error('Marker must be used within a Map')
  }

  const { map, google, mapType, is3D } = mapContext

  const assetsIconGltf = assetsContext.icons.threeGltf[icon]

  useEffect(() => {
    if (!map || !google) {
      return
    }

    if (is3D) {
      if (onDrag || onDragEnd || onDragStart) {
        console.warn('3D markers do not support drag events yet')
      }
      if (customIcon) {
        console.warn('3D markers do not support custom icons yet')
      }
      if (!assetsIconGltf) {
        return
      }
      let scene: Scene
      let renderer: WebGLRenderer
      let camera: PerspectiveCamera
      let loader: GLTFLoader
      const webglOverlayView = new google.maps.WebGLOverlayView()

      webglOverlayView.onAdd = () => {
        scene = new Scene()

        camera = new PerspectiveCamera()

        const ambientLight = new AmbientLight(0xffffff, 0.75)
        scene.add(ambientLight)

        const directionalLight = new DirectionalLight(0xffffff, 0.25)
        directionalLight.position.set(0.5, -1, 0.5)
        scene.add(directionalLight)

        loader = new GLTFLoader()
        const gltfScene = SkeletonUtils.clone(assetsIconGltf.scene)
        const gltfScale = 2 * scale
        gltfScene.scale.set(gltfScale, gltfScale, gltfScale)
        gltfScene.rotation.x = Math.PI
        scene.add(gltfScene)
      }

      webglOverlayView.onContextRestored = ({ gl }) => {
        renderer = new WebGLRenderer({
          canvas: gl.canvas,
          context: gl,
          ...gl.getContextAttributes(),
        })
        renderer.autoClear = false
      }

      webglOverlayView.onDraw = ({ gl, transformer }): void => {
        const latLngAltitudeLiteral: google.maps.LatLngAltitudeLiteral = {
          lat: typeof position.lat === 'function' ? position.lat() : position.lat,
          lng: typeof position.lng === 'function' ? position.lng() : position.lng,
          altitude: 100,
        }

        const matrix = transformer.fromLatLngAltitude(latLngAltitudeLiteral)
        camera.projectionMatrix = new Matrix4().fromArray(matrix)

        webglOverlayView.requestRedraw()
        renderer.render(scene, camera)

        renderer.resetState()
      }

      webglOverlayView.onRemove = () => {
        renderer?.dispose()
      }

      webglOverlayView.setMap(map)

      return () => {
        webglOverlayView.setMap(null)
      }
    }

    const marker = new google.maps.Marker({
      position,
      icon: (customIcon as google.maps.Icon | google.maps.Symbol | undefined) ?? {
        path: mapIcons[icon].svg.definition.icon[4] as string,
        fillColor: color,
        fillOpacity: 1,
        anchor: new google.maps.Point(
          mapIcons[icon].svg.definition.icon[0] / 2,
          mapIcons[icon].svg.definition.icon[1],
        ),
        strokeWeight: mapType == 'satellite' || mapType == 'hybrid' ? 2 : 1,
        strokeColor: borderColor ?? '#fff',
        strokeOpacity: 1,
        scale: 0.05 * scale,
      },
      draggable: onDrag || onDragEnd || onDragStart ? true : false,
      title,
      map,
      label,
      zIndex,
    })

    if (onClick) {
      marker.addListener('click', (event: google.maps.MapMouseEvent) => {
        onClick(event, marker)
      })
    }
    if (onDrag) {
      marker.addListener('drag', (event: google.maps.MapMouseEvent) => {
        onDrag(event, marker)
      })
    }
    if (onDragEnd) {
      marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
        onDragEnd(event, marker)
      })
    }
    if (onDragStart) {
      marker.addListener('dragstart', (event: google.maps.MapMouseEvent) => {
        onDragStart(event, marker)
      })
    }

    return () => {
      marker.setMap(null)
    }
  }, [
    map,
    google,
    position,
    icon,
    title,
    color,
    scale,
    borderColor,
    is3D,
    mapType,
    assetsIconGltf,
    onClick,
    onDrag,
    onDragEnd,
    onDragStart,
    customIcon,
    zIndex,
  ])

  return null
}
